import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { X, Loader2, Lock, Tag, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { openLiveChat } from "../lib/chat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const CheckoutModal = ({ open, lookupKey, title, price, onClose }) => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setSubmitting(false);
  }, [open, lookupKey]);

  const proceed = async () => {
    if (!EMAIL_RE.test(email.trim())) {
      toast.error("Enter a valid email so we can deliver your order.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        lookup_key: lookupKey,
        quantity: 1,
        origin_url: window.location.origin,
        email: email.trim(),
        ref: localStorage.getItem("gtg_ref") || undefined,
      });
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
      else throw new Error("no url");
    } catch {
      toast.error("Couldn't open checkout. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="checkout-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] flex items-center justify-center px-4"
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={submitting ? undefined : onClose} />
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 w-full max-w-md border border-white/10 bg-[#0a0a0a] p-8 lg:p-10"
          >
            <button
              data-testid="checkout-close-button"
              onClick={onClose}
              disabled={submitting}
              className="absolute top-5 right-5 text-white/40 hover:text-white transition-colors disabled:opacity-40"
            >
              <X size={20} />
            </button>

            <span className="font-mono text-xs uppercase tracking-[0.3em] text-accent">// Secure Checkout</span>
            <h3 className="mt-4 font-display font-black uppercase tracking-tighter text-2xl leading-tight">{title}</h3>

            <div className="mt-5 flex items-baseline gap-3">
              <span data-testid="checkout-price" className="font-display font-black text-4xl">{price || "…"}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40">one-time</span>
            </div>

            <label className="mt-7 block font-mono text-[11px] uppercase tracking-[0.2em] text-white/50">Your email</label>
            <input
              data-testid="checkout-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && proceed()}
              placeholder="you@ghosted.com"
              className="mt-2 w-full bg-[#050505] border border-white/15 px-4 py-3.5 font-body text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-accent transition-colors"
            />
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.12em] text-white/30">We send your pack + receipt here</p>

            <div className="mt-5 flex items-start gap-2.5 border border-accent/30 bg-accent/[0.06] px-4 py-3">
              <Tag size={14} className="text-accent mt-0.5 flex-shrink-0" />
              <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-white/60 leading-relaxed">
                Code <span className="text-accent font-bold">GHOST20</span> is applied automatically, 20% off. No need to type it. Military & first-responder codes can still be entered at checkout.
              </p>
            </div>

            <button
              data-testid="checkout-continue-button"
              onClick={proceed}
              disabled={submitting}
              className="group relative mt-7 w-full inline-flex items-center justify-center gap-3 bg-accent text-white font-mono uppercase tracking-[0.15em] text-sm px-8 py-5 overflow-hidden disabled:opacity-70"
            >
              <span className="absolute inset-0 bg-white translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]" />
              <span className="relative z-10 flex items-center gap-2 group-hover:text-black transition-colors duration-300">
                {submitting ? <><Loader2 size={16} className="animate-spin" /> Opening secure checkout…</> : <><Lock size={14} /> Continue to Checkout</>}
              </span>
            </button>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.15em] text-white/30 text-center">
              Secure checkout by Stripe · Card · Instant delivery
            </p>
            <button
              type="button"
              data-testid="checkout-contact-link"
              onClick={openLiveChat}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-accent transition-colors"
            >
              <MessageCircle size={13} /> Questions? Chat with us
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
